<template>
  <div class="production-calendar">
    <div class="main-content">
      <div class="calendar-container" ref="calendarRoot">
        <vue-cal
          ref="vuecal"
          :key="calendarKey"
          class="vuecal-custom production"
          :locale="locale"
          :events="filteredEvents"
          default-view="week"
          :disable-views="['years']"
          :time-from="0"
          :time-to="(24 * 60) - 1"
          :time-step="15"
          :snap-to-time="15"
          today-button
          events-on-month-view
          :editable-events="{ drag: true, resize: true }"
          :dblclick-to-navigate="false"
          @event-change="onEventChange"
          @view-change="onViewChange($event)"
          @ready="onCalendarReady"
          @event-click="onEventClick"
          @event-duration-change="onEventDurationChange"
          @cell-click="() => {}"
          @cell-dblclick="() => {}"
          :week-starts-on="1"
        >
          <template #weekday-heading="{ heading }">
            <span class="full">{{ formatWeekdayLabel(heading.date, 'full') }}</span>
            <span class="small">{{ formatWeekdayLabel(heading.date, 'short') }}</span>
            <span class="xsmall">{{ formatWeekdayLabel(heading.date, 'narrow') }}</span>
            <span>&nbsp;{{ heading.date instanceof Date ? heading.date.getDate() : '' }}</span>
          </template>
        </vue-cal>
      </div>

      <div class="filters-container">
        <CalendarFilters
          :available-formats="availableFormats"
          :available-packagings="availablePackagings"
          :available-custodies="availableCustodies"
          :available-process-plants="availableProcessPlants"
          :format-filters="formatFilters"
          :packaging-filters="packagingFilters"
          :custody-filters="custodyFilters"
          :process-plant-filters="processPlantFilters"
          @toggle-format-filter="toggleFormatFilter"
          @toggle-packaging-filter="togglePackagingFilter"
          @toggle-custody-filter="toggleCustodyFilter"
          @toggle-process-plant-filter="toggleProcessPlantFilter"
        />

        <TonnageSummary
          :total-tonnage="totalTonnage"
          :event-count="visibleEventCount"
        />

        <IngredientsBalance
          :visible-order-ids="visibleOrderIds"
        />
      </div>
    </div>

    <EventInfoModal
      :visible="showEventInfoModal"
      :event-info="eventInfo"
      :allow-process-plant-edit="eventInfo?.editableProcessPlant"
      :process-plant-options="processPlantOptions"
      :allow-duration-edit="Boolean(eventInfo?.editableDuration)"
      :min-duration="15"
      :max-duration="240"
      :duration-step="15"
      :show-notes="false"
      @close="closeEventInfoModal"
      @save="saveEventDetails"
      @update-notes="updateEventNotes"
      @update-process-plant="updateModalProcessPlant"
      @update-duration="updateModalDuration"
    />
  </div>
</template>

<script>
/* eslint-disable */
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import axios from 'axios'

import CalendarFilters from './CalendarFilters.vue'
import TonnageSummary from './TonnageSummary.vue'
import IngredientsBalance from './IngredientsBalance.vue'
import EventInfoModal from './EventInfoModal.vue'
import useEventColors from '../composables/useEventColors.js'

const ensureVueCalMultiDayDragSupport = () => {
  if (!VueCal || !VueCal.components) return
  const cellComponent = VueCal.components['vuecal-cell'] || VueCal.components.VuecalCell
  if (!cellComponent || !cellComponent.components) return
  const eventComponent = cellComponent.components && cellComponent.components.Event
  if (!eventComponent || eventComponent.__alisurMultiDayDragPatched) return

  const componentOptions = eventComponent.options || eventComponent
  if (!componentOptions || !componentOptions.computed || typeof componentOptions.computed.draggable !== 'function') return

  const originalDraggable = componentOptions.computed.draggable
  componentOptions.computed.draggable = function patchedDraggable () {
    const originalResult = originalDraggable.call(this)
    if (originalResult) return originalResult

    const event = this.event || {}
    const editEvents = this.vuecal && this.vuecal.editEvents

    const dragEnabled = Boolean(editEvents && editEvents.drag)
    const eventAllowsDrag = event.draggable !== false
    const isBackground = Boolean(event.background)

    if (!dragEnabled || !eventAllowsDrag || isBackground) return originalResult
    return true
  }

  eventComponent.__alisurMultiDayDragPatched = true
}

ensureVueCalMultiDayDragSupport()

export default {
  name: 'ProductionCalendar',
  components: {
    VueCal,
    CalendarFilters,
    TonnageSummary,
    IngredientsBalance,
    EventInfoModal,
  },

  data () {
    return {
      locale: 'es',
      events: [],
      calendarKey: 0,
      formatFilters: {},
      packagingFilters: {},
      custodyFilters: {},
      processPlantFilters: {},
      dynamicColorClasses: new Set(),
      showEventInfoModal: false,
      eventInfo: null,
      selectedEvent: null,
      lastClickTime: 0,
      lastClickedEvent: null,
      processPlantOptions: [
        { value: 'line1', label: 'Línea 1' },
        { value: 'line2', label: 'Línea 2' },
        { value: 'reception', label: 'Recepción' },
      ],
      pendingDurationChanges: new Set(),
      isInitialLoad: true,
      isAttachingNavigation: false,
      navigationWrapperEl: null,
      prevDayButtonEl: null,
      nextDayButtonEl: null,
      resizeObserver: null,
      loadedRange: null,
      lastRequestToken: 0,
      pendingRange: null,
    }
  },

  setup () {
    const { getFormatColor, getEventClass, ensureColorClass } = useEventColors()
    return { getFormatColor, getEventClass, ensureColorClass }
  },

  computed: {
    filteredEvents () {
      return this.events.filter(event => {
        if (!this.formatFilters[event.format]) return false
        if (!this.packagingFilters[event.packaging]) return false
        const custody = event.company_from || event.company_to
        if (custody && !this.custodyFilters[custody]) return false
        if (event.process_plant && !this.processPlantFilters[event.process_plant]) return false
        return true
      })
    },

    visibleEventCount () {
      return this.filteredEvents.length
    },

    totalTonnage () {
      return this.filteredEvents.reduce((sum, event) => sum + (event.tonnage || 0), 0)
    },

    availableFormats () {
      return this.collectUnique(event => event.format)
    },

    availablePackagings () {
      return this.collectUnique(event => event.packaging)
    },

    availableCustodies () {
      const companies = new Set()
      this.events.forEach(event => {
        if (event.company_from) companies.add(event.company_from)
        if (event.company_to) companies.add(event.company_to)
      })
      return Array.from(companies).sort()
    },

    availableProcessPlants () {
      return this.collectUnique(event => event.process_plant)
    },

    visibleOrderIds () {
      return this.filteredEvents.map(event => event.order_id).filter(id => id != null)
    },
  },

  watch: {
    availableFormats (list) { this.updateFilterState(list, this.formatFilters, value => { this.$set(this.formatFilters, value, true) }) },
    availablePackagings (list) { this.updateFilterState(list, this.packagingFilters, value => { this.$set(this.packagingFilters, value, true) }) },
    availableCustodies (list) { this.updateFilterState(list, this.custodyFilters, value => { this.$set(this.custodyFilters, value, true) }) },
    availableProcessPlants (list) { this.updateFilterState(list, this.processPlantFilters, value => { this.$set(this.processPlantFilters, value, true) }) },
  },

  mounted () {
    const today = new Date()
    const monday = this.getMondayOfWeek(today)
    const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)
    sunday.setHours(0, 0, 0, 0)
    const start = this.formatDateParam(monday)
    const end = this.formatDateParam(sunday)
    this.loadEvents(start, end)
    this.$nextTick(() => {
      this.setupNativeDoubleClickListener()
      this.setupCustomNavigation()
      this.setupEventResizeObserver()
    })
  },

  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },

  methods: {
    collectUnique (fn) {
      const values = new Set()
      this.events.forEach(event => {
        const value = fn(event)
        if (value) values.add(value)
      })
      return Array.from(values).sort()
    },

    updateFilterState (availableList, container, ensureFn) {
      availableList.forEach(value => {
        if (!(value in container)) ensureFn(value)
      })
      Object.keys(container).forEach(key => {
        if (!availableList.includes(key)) this.$delete(container, key)
      })
    },

    toggleFormatFilter ({ format, checked }) {
      this.$set(this.formatFilters, format, checked)
    },
    togglePackagingFilter ({ packaging, checked }) {
      this.$set(this.packagingFilters, packaging, checked)
    },
    toggleCustodyFilter ({ custody, checked }) {
      this.$set(this.custodyFilters, custody, checked)
    },
    toggleProcessPlantFilter ({ processPlant, checked }) {
      this.$set(this.processPlantFilters, processPlant, checked)
    },

    parseToLocalDate (input) {
      if (!input) return null
      if (input instanceof Date) return input
      const value = String(input)
      if (/[zZ]|[+-]\d{2}:\d{2}$/.test(value)) return new Date(value)
      const [datePart, timePart = '00:00:00'] = value.trim().split(/[T ]/)
      const [y, m, d] = datePart.split('-').map(n => parseInt(n, 10))
      const [hh = 0, mm = 0, ss = 0] = timePart.split(':').map(n => parseInt(n, 10))
      return new Date(y, m - 1, d, hh, mm, ss, 0)
    },

    toIsoWithOffsetLocal (date) {
      const dt = date instanceof Date ? date : new Date(date)
      const pad = n => `${Math.abs(n)}`.padStart(2, '0')
      const y = dt.getFullYear()
      const m = pad(dt.getMonth() + 1)
      const d = pad(dt.getDate())
      const hh = pad(dt.getHours())
      const mm = pad(dt.getMinutes())
      const ss = pad(dt.getSeconds())
      const offset = -dt.getTimezoneOffset()
      const sign = offset >= 0 ? '+' : '-'
      const oh = pad(Math.floor(Math.abs(offset) / 60))
      const om = pad(Math.abs(offset) % 60)
      return `${y}-${m}-${d}T${hh}:${mm}:${ss}${sign}${oh}:${om}`
    },

    csrfHeaders () {
      return {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json',
      }
    },

    async loadEvents (startDate = null, endDate = null) {
      const params = {}
      if (startDate) params.start_date = startDate
      if (endDate) params.end_date = endDate
      const requestToken = ++this.lastRequestToken
      const isRangeRequest = Boolean(startDate && endDate)
      if (isRangeRequest) {
        this.pendingRange = { start: startDate, end: endDate }
      } else if (!startDate && !endDate) {
        this.pendingRange = null
      }

      try {
        const { data } = await axios.get('/api/v1/production_calendar_events', { params, headers: this.csrfHeaders() })
        if (requestToken === this.lastRequestToken) {
          this.events = data.map(raw => this.normalizeEvent(raw))
          if (startDate && endDate) {
            this.loadedRange = { start: startDate, end: endDate }
          } else {
            this.loadedRange = null
          }
          this.pendingRange = null
          this.$nextTick(() => this.attachDoubleClickToEvents())
        }
      } catch (error) {
        console.error('Error cargando eventos producción:', error)
        if (requestToken === this.lastRequestToken) {
          this.pendingRange = null
        }
      }
    },

    normalizeEvent (raw) {
      const start = this.parseToLocalDate(raw.start)
      const end = this.parseToLocalDate(raw.end)
      const baseClass = raw.class || [`status-${raw.status}`, this.getEventClass(raw.format, raw.packaging)].filter(Boolean).join(' ')

      const durationMinutes = Math.max(15, Math.round((end - start) / 60000))
      const title = this.buildEventTitle(raw)

      let classes = baseClass
      if (raw.color_hex) {
        const colorClass = this.ensureColorClass(raw.color_hex, this.dynamicColorClasses)
        classes = [baseClass, colorClass].filter(Boolean).join(' ')
      }

      return {
        ...raw,
        start,
        end,
        class: classes,
        duration: durationMinutes,
        duration_minutes: durationMinutes,
        title,
      }
    },

    buildEventTitle (event) {
      const orderId = event.order_id || event.id || 'N/A'
      const weight = event.tonnage_display || ''
      const clientName = event.billing_name || event.company_to || 'Sin cliente'
      const formulaName = event.formula_name || 'Sin fórmula'
      const processPlantLabel = this.getProcessPlantLabel(event.process_plant)
      const durationMinutes = event.duration_minutes || 0
      const durationHours = Math.floor(durationMinutes / 60)
      const durationMins = durationMinutes % 60
      const durationText = durationHours > 0
        ? `${durationHours}h ${durationMins}m`
        : `${durationMins}m`

      return `<div class=\"event-wrapper\" data-order-id=\"${orderId}\">
        <div class=\"event-line-1\">
          <span class=\"event-id\">${orderId}</span>
          <span class=\"event-weight\">${weight}</span>
        </div>
        <div class=\"event-line-2\"><span class=\"event-client\">${clientName}</span></div>
        <div class=\"event-line-3\"><span class=\"event-formula\">${formulaName}</span></div>
        <div class=\"event-line-4\"><span class=\"event-plant\">${processPlantLabel}</span></div>
        <div class=\"event-line-5\"><span class=\"event-duration\">${durationText}</span></div>
      </div>`
    },

    onCalendarReady () {
      if (this.isInitialLoad) {
        this.isInitialLoad = false
        this.resetWeekViewTo(new Date())
      }
      this.$nextTick(() => {
        this.attachDoubleClickToEvents()
        setTimeout(() => {
          this.setupCustomNavigation()
        }, 100)
      })
    },

    onViewChange ({ startDate, endDate, view }) {
      console.log("Cambio de vista a", view ? view.id : 'desconocida', "en", new Date())
      const startDateObj = typeof startDate === 'string' ? new Date(startDate) : startDate
      const endDateObj = typeof endDate === 'string' ? new Date(endDate) : endDate
      if (startDateObj instanceof Date && !Number.isNaN(startDateObj.getTime()) &&
          endDateObj instanceof Date && !Number.isNaN(endDateObj.getTime())) {
        const normalizedStart = new Date(startDateObj)
        const normalizedEnd = new Date(endDateObj)
        normalizedStart.setHours(0, 0, 0, 0)
        normalizedEnd.setHours(0, 0, 0, 0)
        this.loadEventsForDateRange(normalizedStart, normalizedEnd)
      }

      this.$nextTick(() => {
        this.attachDoubleClickToEvents()
        setTimeout(() => {
          this.attachNavigationListeners()
        }, 100)
      })
    },

    resetWeekViewTo(date) {
      const vuecal = this.$refs.vuecal
      if (!vuecal) return
      const monday = this.getMondayOfWeek(date)
      console.log("Reseteando vista semanal a", monday)
      const endDate = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)
      endDate.setHours(0, 0, 0, 0)
      if (vuecal.updateMutableDate) vuecal.updateMutableDate(new Date(monday))
      if (!vuecal.view || vuecal.view.id !== 'week') vuecal.switchView('week')
      if (vuecal.view) {
        vuecal.view.startDate = new Date(monday)
        vuecal.view.endDate = new Date(endDate)
        vuecal.$forceUpdate()
      }
      this.loadEventsForDateRange(monday, endDate)
      this.updateWeekdayLabels(monday)
    },

    getMondayOfWeek (date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      const day = d.getDay()
      const diff = d.getDate() - day + (day === 0 ? -6 : 1)
      d.setDate(diff)
      d.setHours(0, 0, 0, 0)
      return new Date(d)
    },

    getEventKey (event) {
      if (!event) return null
      const key = event.order_id ?? event.id ?? event._id ?? null
      return key != null ? String(key) : null
    },

    onEventDurationChange ({ event }) {
      const key = this.getEventKey(event)
      if (key) this.pendingDurationChanges.add(key)
    },

    getEventDurationMinutes (eventLike) {
      if (!eventLike) return null
      const start = this.parseToLocalDate(eventLike.start)
      const end = this.parseToLocalDate(eventLike.end)
      if (!(start instanceof Date) || Number.isNaN(start.getTime())) return null
      if (!(end instanceof Date) || Number.isNaN(end.getTime())) return null
      const diff = Math.round((end - start) / 60000)
      return Number.isFinite(diff) ? diff : null
    },

    normalizeDurationValue (value) {
      if (value === null || value === undefined) return null
      if (typeof value === 'number' && Number.isFinite(value)) return value
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
      }
      return null
    },

    sanitizeDurationMinutes (value) {
      const step = 15
      const min = 15
      const max = 240
      const normalized = this.normalizeDurationValue(value)
      if (normalized === null) return min
      const clamped = Math.min(max, Math.max(min, normalized))
      return Math.round(clamped / step) * step
    },

    formatDateParam (date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },

    formatWeekdayLabel (date, variant = 'full') {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
      const locales = this.locale || 'es'
      const optionMap = {
        full: { weekday: 'long' },
        short: { weekday: 'short' },
        narrow: { weekday: 'narrow' },
      }
      const options = optionMap[variant] || optionMap.full
      const formatted = date.toLocaleDateString(locales, options)
      const cleaned = formatted.replace(/\.$/, '')
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
    },

    async onEventChange ({ event, originalEvent }) {
      const key = this.getEventKey(event)
      const triggeredByResize = key ? this.pendingDurationChanges.has(key) : false
      if (key) this.pendingDurationChanges.delete(key)

      let start = this.parseToLocalDate(event.start)
      let end = this.parseToLocalDate(event.end)
      if (!(start instanceof Date) || Number.isNaN(start.getTime())) return
      if (!(end instanceof Date) || Number.isNaN(end.getTime())) return

      const originalDurationRaw = this.getEventDurationMinutes(originalEvent)
        ?? this.normalizeDurationValue(originalEvent?.duration_minutes)
        ?? this.normalizeDurationValue(event.duration_minutes)
      const sanitizedOriginalDuration = originalDurationRaw != null
        ? this.sanitizeDurationMinutes(originalDurationRaw)
        : null

      const rawDurationFromEvent = Math.max(0, Math.round((end - start) / 60000))
      let durationMinutes = triggeredByResize
        ? this.sanitizeDurationMinutes(rawDurationFromEvent)
        : (sanitizedOriginalDuration ?? this.sanitizeDurationMinutes(rawDurationFromEvent))

      const minDuration = 15
      const maxDuration = 240
      if (durationMinutes < minDuration) durationMinutes = minDuration
      if (durationMinutes > maxDuration) durationMinutes = maxDuration

      end = new Date(start.getTime() + durationMinutes * 60000)

      const minutesFromDate = date => (date.getHours() * 60) + date.getMinutes()
      const minutesOrFullDay = date => {
        const minutes = minutesFromDate(date)
        if (minutes === 0 && date > start) return 1440
        return minutes
      }

      event.start = start
      event.end = end
      event.duration_minutes = durationMinutes
      event.duration = durationMinutes
      if (Object.prototype.hasOwnProperty.call(event, 'startTimeMinutes')) {
        event.startTimeMinutes = minutesFromDate(start)
      }
      if (Object.prototype.hasOwnProperty.call(event, 'endTimeMinutes')) {
        event.endTimeMinutes = minutesOrFullDay(end)
      }
      if (Object.prototype.hasOwnProperty.call(event, 'daysCount')) {
        const normalizeToMidnight = value => new Date(value.getFullYear(), value.getMonth(), value.getDate())
        const startMidnight = normalizeToMidnight(start)
        const endMidnight = normalizeToMidnight(end)
        const diffDays = Math.round((endMidnight - startMidnight) / (24 * 60 * 60 * 1000)) + 1
        if (diffDays > 0) event.daysCount = diffDays
      }

      event.title = this.buildEventTitle(event)

      try {
        const { data } = await axios.patch(
          `/api/v1/production_calendar_events/${event.order_id}`,
          {
            event: {
              start: this.toIsoWithOffsetLocal(start),
              end: this.toIsoWithOffsetLocal(end),
              duration_minutes: durationMinutes,
            },
          },
          { headers: this.csrfHeaders() }
        )

        if (data?.event) {
          this.updateEventFromResponse(data.event)
        } else {
          this.reloadCurrentRange()
        }
      } catch (error) {
        console.error('No se pudo actualizar el evento producción:', error)
        this.reloadCurrentRange()
      }
    },

    updateEventFromResponse (updated) {
      const index = this.events.findIndex(ev => String(ev.order_id || ev.id) === String(updated.id || updated.order_id))
      if (index === -1) return
      const normalized = this.normalizeEvent(updated)
      this.$set(this.events, index, normalized)
    },

    onEventClick (event) {
      const currentTime = Date.now()
      if (this.lastClickedEvent && (currentTime - this.lastClickTime) < 400) {
        const sameEvent = (this.lastClickedEvent.id === (event.id || event.order_id))
          || (this.lastClickedEvent.order_id === (event.id || event.order_id))
        if (sameEvent) {
          this.showEventInfo(event)
          this.lastClickedEvent = null
          this.lastClickTime = 0
          return false
        }
      }

      this.lastClickTime = currentTime
      this.lastClickedEvent = event
      return false
    },

    setupEventResizeObserver () {
      const calendarEl = this.$refs.vuecal?.$el
      if (!calendarEl) {
        setTimeout(() => this.setupEventResizeObserver(), 100)
        return
      }

      // Observar cambios en los atributos de estilo de los eventos
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' &&
              (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            const eventElement = mutation.target
            if (eventElement.classList.contains('vuecal__event')) {
              this.updateEventDurationDisplay(eventElement)
            }
          }
        })
      })

      // Observar todos los eventos existentes
      const observeEvents = () => {
        const eventElements = calendarEl.querySelectorAll('.vuecal__event')
        eventElements.forEach(eventEl => {
          observer.observe(eventEl, {
            attributes: true,
            attributeFilter: ['style', 'class']
          })
        })
      }

      // Observar cambios en el calendario para capturar nuevos eventos
      const calendarObserver = new MutationObserver(() => {
        observeEvents()
      })

      calendarObserver.observe(calendarEl, {
        childList: true,
        subtree: true
      })

      observeEvents()
      this.resizeObserver = { disconnect: () => { observer.disconnect(); calendarObserver.disconnect() } }
    },

    updateEventDurationDisplay (eventElement) {
      // Obtener el ID del evento
      const wrapper = eventElement.querySelector('[data-order-id]')
      if (!wrapper) return

      const orderId = wrapper.getAttribute('data-order-id')
      const event = this.events.find(ev => String(ev.order_id || ev.id) === String(orderId))
      if (!event) return

      // Calcular la altura del elemento en píxeles
      const height = eventElement.offsetHeight

      // Obtener la altura de una celda de tiempo (15 minutos)
      // El calendario tiene time-step de 15 minutos
      const calendarEl = this.$refs.vuecal?.$el
      if (!calendarEl) return

      // Buscar la altura de una celda de tiempo
      const timeCell = calendarEl.querySelector('.vuecal__time-cell')
      if (!timeCell) return

      const timeCellHeight = timeCell.offsetHeight
      const timeStep = 15 // minutos por celda

      // Calcular la duración basándose en la altura
      const durationMinutes = Math.max(15, Math.round((height / timeCellHeight) * timeStep))

      // Calcular el texto de duración
      const durationHours = Math.floor(durationMinutes / 60)
      const durationMins = durationMinutes % 60
      const durationText = durationHours > 0
        ? `${durationHours}h ${durationMins}m`
        : `${durationMins}m`

      // Actualizar el DOM
      const durationLine = wrapper.querySelector('.event-line-5 .event-duration')
      if (durationLine && durationLine.textContent !== durationText) {
        durationLine.textContent = durationText
      }
    },

    setupNativeDoubleClickListener () {
      const observer = new MutationObserver(() => { this.attachDoubleClickToEvents() })
      const calendarContainer = this.$refs.vuecal?.$el
      if (calendarContainer) {
        observer.observe(calendarContainer, { childList: true, subtree: true })
        this.attachDoubleClickToEvents()
      }
    },

    attachDoubleClickToEvents () {
      const calendarEl = this.$refs.vuecal?.$el
      if (!calendarEl) return
      const eventElements = calendarEl.querySelectorAll('.vuecal__event')
      eventElements.forEach(eventEl => {
        const wrapper = eventEl.querySelector('[data-order-id]')
        if (wrapper && wrapper.getAttribute) {
          const orderId = wrapper.getAttribute('data-order-id')
          if (orderId) {
            eventEl.dataset.orderId = orderId
          } else {
            delete eventEl.dataset.orderId
          }
        } else if (eventEl.dataset && eventEl.dataset.orderId) {
          delete eventEl.dataset.orderId
        }
        eventEl.removeEventListener('dblclick', this.handleNativeDoubleClick)
        eventEl.addEventListener('dblclick', this.handleNativeDoubleClick)
      })
    },

    handleNativeDoubleClick (nativeEvent) {
      nativeEvent.preventDefault()
      nativeEvent.stopPropagation()

      let eventId = null

      const currentTarget = nativeEvent.currentTarget
      if (currentTarget && currentTarget.dataset && currentTarget.dataset.orderId) {
        eventId = currentTarget.dataset.orderId
      }

      if (!eventId && nativeEvent.target && typeof nativeEvent.target.closest === 'function') {
        const wrapper = nativeEvent.target.closest('[data-order-id]')
        if (wrapper) eventId = wrapper.getAttribute('data-order-id')
      }

      if (eventId) {
        const found = this.events.find(ev => String(ev.order_id || ev.id) === String(eventId))
        if (found) {
          this.showEventInfo(found)
          return
        }
      }

      if (this.lastClickedEvent) {
        this.showEventInfo(this.lastClickedEvent)
      }
    },

    async showEventInfo (event) {
      if (!event) return
      const processPlantLabel = this.getProcessPlantLabel(event.process_plant)
      const rawDuration = this.normalizeDurationValue(event.duration_minutes) ?? this.getEventDurationMinutes(event)
      const durationMinutes = this.sanitizeDurationMinutes(rawDuration)
      event.duration_minutes = durationMinutes
      this.eventInfo = {
        id: event.order_id || event.id,
        empresa_cliente: event.billing_name || event.company_to || 'Sin cliente',
        custodia: event.company_from || 'Sin custodia',
        envase: this.getPackagingLabel(event.packaging),
        formato: this.getFormatLabel(event.format),
        peso_estimado: event.tonnage ? `${event.tonnage.toFixed(1)} toneladas` : 'No especificado',
        start: event.start,
        end: event.end,
        process_plant: event.process_plant,
        process_plant_label: processPlantLabel,
        planta_proceso: processPlantLabel,
        notes: event.notes || '',
        original_notes: event.notes || '',
        original_process_plant: event.process_plant,
        editableProcessPlant: Boolean(event.editable),
        duration_minutes: durationMinutes,
        original_duration_minutes: durationMinutes,
        editableDuration: Boolean(event.editable),
        ingredients: [],
      }
      this.selectedEvent = event
      this.showEventInfoModal = true

      try {
        const orderId = event.order_id || event.id
        const { data } = await axios.get(`/api/v1/calendar_events/${orderId}/ingredients`, { headers: this.csrfHeaders() })
        this.eventInfo.ingredients = data.ingredients || []
      } catch (error) {
        console.error('Error al cargar ingredientes del evento producción:', error)
        this.eventInfo.ingredients = []
      }
    },

    closeEventInfoModal () {
      this.showEventInfoModal = false
      this.eventInfo = null
      this.selectedEvent = null
    },

    updateEventNotes (notes) {
      if (this.eventInfo) this.eventInfo.notes = notes
    },

    updateModalProcessPlant (value) {
      if (this.eventInfo) this.eventInfo.process_plant = value
    },

    updateModalDuration (value) {
      if (!this.eventInfo) return
      const sanitized = this.sanitizeDurationMinutes(value)
      this.eventInfo.duration_minutes = sanitized
      if (this.selectedEvent) this.selectedEvent.duration_minutes = sanitized
    },

    async saveEventDetails () {
      if (!this.eventInfo) return
      const orderId = this.eventInfo.id

      const requests = []
      const eventPayload = {}

      const processPlantChanged = this.eventInfo.process_plant !== this.eventInfo.original_process_plant
      if (processPlantChanged && this.eventInfo.editableProcessPlant) {
        eventPayload.process_plant = this.eventInfo.process_plant
      }

      const sanitizedDuration = this.sanitizeDurationMinutes(this.eventInfo.duration_minutes)
      const originalSanitizedDuration = this.sanitizeDurationMinutes(this.eventInfo.original_duration_minutes)
      const durationChanged = sanitizedDuration !== originalSanitizedDuration

      if (durationChanged) {
        const baseEvent = (this.selectedEvent && String(this.selectedEvent.order_id || this.selectedEvent.id) === String(orderId))
          ? this.selectedEvent
          : this.events.find(ev => String(ev.order_id || ev.id) === String(orderId))
        let startDate = this.parseToLocalDate(baseEvent?.start)
        if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
          startDate = this.parseToLocalDate(this.eventInfo.start)
        }
        if (startDate instanceof Date && !Number.isNaN(startDate.getTime())) {
          const newEnd = new Date(startDate.getTime() + sanitizedDuration * 60000)
          eventPayload.start = this.toIsoWithOffsetLocal(startDate)
          eventPayload.end = this.toIsoWithOffsetLocal(newEnd)
          eventPayload.duration_minutes = sanitizedDuration
          if (baseEvent) {
            baseEvent.duration_minutes = sanitizedDuration
            baseEvent.duration = sanitizedDuration
            baseEvent.end = newEnd
          }
          this.eventInfo.end = newEnd
        } else {
          console.warn('No se pudo actualizar la duración: inicio de evento inválido para', orderId)
        }
      }

      if (Object.keys(eventPayload).length) {
        requests.push(axios.patch(
          `/api/v1/production_calendar_events/${orderId}`,
          { event: eventPayload },
          { headers: this.csrfHeaders() }
        ))
      }

      if (this.eventInfo.notes !== this.eventInfo.original_notes) {
        requests.push(axios.put(
          `/api/v1/calendar_events/${orderId}`,
          { notes: this.eventInfo.notes },
          { headers: this.csrfHeaders() }
        ))
      }

      try {
        if (requests.length) await Promise.all(requests)
        this.closeEventInfoModal()
        this.reloadCurrentRange()
      } catch (error) {
        console.error('No se pudo guardar la información del evento producción:', error)
      }
    },

    setupCustomNavigation() {
      this.$nextTick(() => {
        setTimeout(() => { this.attachNavigationListeners() }, 1)
      })
    },

    attachNavigationListeners() {
      if (this.isAttachingNavigation) {
        return
      }

      this.isAttachingNavigation = true

      const calendarEl = this.$refs.vuecal?.$el
      if (!calendarEl) {
        this.isAttachingNavigation = false
        setTimeout(() => this.attachNavigationListeners(), 100)
        return
      }

      const allPrevButtons = Array.from(calendarEl.querySelectorAll('.vuecal__arrow.vuecal__arrow--prev'))
      const allNextButtons = Array.from(calendarEl.querySelectorAll('.vuecal__arrow.vuecal__arrow--next'))

      const prevWeekButtons = allPrevButtons.filter(btn => !btn.classList.contains('calendar-day-nav__btn'))
      const nextWeekButtons = allNextButtons.filter(btn => !btn.classList.contains('calendar-day-nav__btn'))

      const prevButton = prevWeekButtons[0] || null
      const nextButton = nextWeekButtons[0] || null

      if (prevWeekButtons.length > 1) prevWeekButtons.slice(1).forEach(btn => btn.remove())
      if (nextWeekButtons.length > 1) nextWeekButtons.slice(1).forEach(btn => btn.remove())
      const todayButton = calendarEl.querySelector('.vuecal__today-btn')

      let navigationWrapper = this.navigationWrapperEl
      if (!navigationWrapper || !navigationWrapper.isConnected) {
        navigationWrapper = prevButton?.parentElement || nextButton?.parentElement || navigationWrapper
        if (navigationWrapper) this.navigationWrapperEl = navigationWrapper
      } else {
        navigationWrapper = this.navigationWrapperEl
      }

      if (!navigationWrapper) {
        this.isAttachingNavigation = false
        setTimeout(() => this.attachNavigationListeners(), 100)
        return
      }

      if (prevButton) {
        prevButton.removeEventListener('click', this.handlePrevNavigation, true)
        prevButton.addEventListener('click', this.handlePrevNavigation, true)
        prevButton.classList.add('calendar-week-nav__btn')
        prevButton.setAttribute('aria-label', 'Semana anterior')
        prevButton.setAttribute('title', 'Semana anterior')
        prevButton.textContent = '<<'
      }

      if (nextButton) {
        nextButton.removeEventListener('click', this.handleNextNavigation, true)
        nextButton.addEventListener('click', this.handleNextNavigation, true)
        nextButton.classList.add('calendar-week-nav__btn')
        nextButton.setAttribute('aria-label', 'Semana siguiente')
        nextButton.setAttribute('title', 'Semana siguiente')
        nextButton.textContent = '>>'
      }

      if (todayButton) {
        todayButton.removeEventListener('click', this.handleTodayNavigation, true)
        todayButton.addEventListener('click', this.handleTodayNavigation, true)
        if (todayButton.parentElement === navigationWrapper ||
            todayButton.parentElement?.classList?.contains('calendar-nav-wrapper')) {
          todayButton.classList.add('calendar-nav__today')
        } else {
          todayButton.classList.remove('calendar-nav__today')
        }
      }

      if (navigationWrapper) {
        navigationWrapper.classList.add('calendar-nav-wrapper')

        const existingPrevDayButtons = Array.from(navigationWrapper.querySelectorAll('.calendar-day-nav__prev'))
          .filter(btn => btn !== this.prevDayButtonEl)
        const existingNextDayButtons = Array.from(navigationWrapper.querySelectorAll('.calendar-day-nav__next'))
          .filter(btn => btn !== this.nextDayButtonEl)
        existingPrevDayButtons.forEach(btn => btn.remove())
        existingNextDayButtons.forEach(btn => btn.remove())

        if (!this.prevDayButtonEl) {
          this.prevDayButtonEl = document.createElement('button')
        }
        if (!this.nextDayButtonEl) {
          this.nextDayButtonEl = document.createElement('button')
        }

        const prevDayButton = this.prevDayButtonEl
        prevDayButton.type = 'button'
        prevDayButton.className = 'vuecal__arrow calendar-day-nav__btn calendar-day-nav__prev'
        prevDayButton.textContent = '<'
        prevDayButton.setAttribute('aria-label', 'Retroceder un día')
        prevDayButton.setAttribute('title', 'Retroceder un día')
        prevDayButton.removeEventListener('click', this.handlePrevDayNavigation, true)
        prevDayButton.addEventListener('click', this.handlePrevDayNavigation, true)

        const nextDayButton = this.nextDayButtonEl
        nextDayButton.type = 'button'
        nextDayButton.className = 'vuecal__arrow calendar-day-nav__btn calendar-day-nav__next'
        nextDayButton.textContent = '>'
        nextDayButton.setAttribute('aria-label', 'Avanzar un día')
        nextDayButton.setAttribute('title', 'Avanzar un día')
        nextDayButton.removeEventListener('click', this.handleNextDayNavigation, true)
        nextDayButton.addEventListener('click', this.handleNextDayNavigation, true)

        let leftGroup = navigationWrapper.querySelector('.calendar-nav-group--left')
        if (!leftGroup) {
          leftGroup = document.createElement('div')
          leftGroup.className = 'calendar-nav-group calendar-nav-group--left'
          navigationWrapper.insertBefore(leftGroup, navigationWrapper.firstChild)
        }

        if (prevButton && prevButton.parentElement !== leftGroup) {
          leftGroup.appendChild(prevButton)
        }
        if (prevDayButton && prevDayButton.parentElement !== leftGroup) {
          leftGroup.appendChild(prevDayButton)
        }

        let rightGroup = navigationWrapper.querySelector('.calendar-nav-group--right')
        if (!rightGroup) {
          rightGroup = document.createElement('div')
          rightGroup.className = 'calendar-nav-group calendar-nav-group--right'
          navigationWrapper.appendChild(rightGroup)
        }

        if (nextDayButton && nextDayButton.parentElement !== rightGroup) {
          rightGroup.appendChild(nextDayButton)
        }
        if (nextButton && nextButton.parentElement !== rightGroup) {
          rightGroup.appendChild(nextButton)
        }
      }

      this.isAttachingNavigation = false
    },

    handlePrevNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.allowDefaultNavigation('prev')
    },

    handleNextNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.allowDefaultNavigation('next')
    },

    handlePrevDayNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.navigateByDays(-1)
    },

    handleNextDayNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.navigateByDays(1)
    },

    handleTodayNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      console.log("Navegando a hoy", new Date())
      this.resetWeekViewTo(new Date())
    },

    navigateByDays(direction) {
      if (!this.$refs.vuecal || !this.$refs.vuecal.view) return
      const currentStartDate = new Date(this.$refs.vuecal.view.startDate)
      currentStartDate.setHours(0, 0, 0, 0)
      const newStartDate = new Date(currentStartDate.getTime() + (direction * 24 * 60 * 60 * 1000))
      newStartDate.setHours(0, 0, 0, 0)
      const newEndDate = new Date(newStartDate.getTime() + 6 * 24 * 60 * 60 * 1000)
      newEndDate.setHours(0, 0, 0, 0)
      if (this.$refs.vuecal.updateMutableDate) this.$refs.vuecal.updateMutableDate(new Date(newStartDate))
      this.$refs.vuecal.view.startDate = new Date(newStartDate)
      this.$refs.vuecal.view.endDate = new Date(newEndDate)
      this.$refs.vuecal.$forceUpdate()
      this.loadEventsForDateRange(newStartDate, newEndDate)
    },

    allowDefaultNavigation(direction) {
      if (direction === 'prev') this.$refs.vuecal.previous()
      else if (direction === 'next') this.$refs.vuecal.next()
    },

    loadEventsForDateRange(startDate, endDate) {
      const start = this.formatDateParam(startDate)
      const end = this.formatDateParam(endDate)
      if (this.loadedRange &&
          this.loadedRange.start === start &&
          this.loadedRange.end === end) {
        return
      }
      if (this.pendingRange &&
          this.pendingRange.start === start &&
          this.pendingRange.end === end) {
        return
      }
      this.loadEvents(start, end)
    },

    reloadCurrentRange () {
      if (this.loadedRange && this.loadedRange.start && this.loadedRange.end) {
        this.loadEvents(this.loadedRange.start, this.loadedRange.end)
      } else {
        this.loadEvents()
      }
    },

    captureOriginalWeekdayLabels () {},
    updateWeekdayLabels () {},
    capitalizeLabel (label) {
      if (!label) return ''
      return label.charAt(0).toUpperCase() + label.slice(1)
    },

    getPackagingLabel (packaging) {
      switch (packaging) {
        case 'granel': return 'Granel'
        case 'ensacado': return 'Ensacado'
        case 'maxisaco': return 'Maxisaco'
        default: return 'Otro'
      }
    },

    getFormatLabel (format) {
      switch (format) {
        case 'entero': return 'Entero'
        case 'harinado': return 'Harinado'
        case 'multiparticle': return 'Multipartícula'
        case 'pelet_3': return 'Pelet 3'
        case 'pelet_6': return 'Pelet 6'
        case 'pelet_8': return 'Pelet 8'
        case 'pelet_12': return 'Pelet 12'
        default: return 'Otro'
      }
    },

    getProcessPlantLabel (value) {
      switch (value) {
        case 'line1': return 'Línea 1'
        case 'line2': return 'Línea 2'
        case 'reception': return 'Recepción'
        default: return 'Sin definir'
      }
    },
  },
}
</script>

<style scoped>
.production-calendar {
  padding: 20px;
  background: #f0f2f5;
  min-height: 100vh;
  box-sizing: border-box;
}

.main-content {
  display: flex;
  gap: 20px;
}

.calendar-container {
  flex: 1 1 auto;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px;
}

.filters-container {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.vuecal-custom.production :deep(.vuecal__event) {
  font-size: 10px;
  font-weight: 500;
  color: #fff !important;
  cursor: pointer;
  border-radius: 3px;
  padding: 2px 4px;
  line-height: 1.1;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.vuecal-custom.production :deep(.vuecal__event.status-produced),
.vuecal-custom.production :deep(.vuecal__event.status-produced .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.status-produced .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.status-produced .vuecal__event-time) {
 transform: none !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important;
}

.vuecal-custom.production :deep(.vuecal__event.status-first_weighing),
.vuecal-custom.production :deep(.vuecal__event.status-first_weighing .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.status-first_weighing .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.status-first_weighing .vuecal__event-time) {
  transform: none !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important;
}

.vuecal-custom.production :deep(.vuecal__event.status-shipped),
.vuecal-custom.production :deep(.vuecal__event.status-shipped .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.status-shipped .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.status-shipped .vuecal__event-time) {
  transform: none !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important;
}

.vuecal-custom.production :deep(.vuecal__event.status-delivered),
.vuecal-custom.production :deep(.vuecal__event.status-delivered .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.status-delivered .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.status-delivered .vuecal__event-time) {
  /* opacity: 0.5 !important; */
  /* filter: saturate(0.4) brightness(0.8); */
}

.vuecal-custom.production :deep(.vuecal__event.format-entero),
.vuecal-custom.production :deep(.vuecal__event.format-entero .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-entero .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-entero .vuecal__event-time) { background: #56B686 !important; }
.vuecal-custom.production :deep(.vuecal__event.format-harinado),
.vuecal-custom.production :deep(.vuecal__event.format-harinado .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-harinado .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-harinado .vuecal__event-time) { background: #D85796 !important; }
.vuecal-custom.production :deep(.vuecal__event.format-multiparticle),
.vuecal-custom.production :deep(.vuecal__event.format-multiparticle .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-multiparticle .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-multiparticle .vuecal__event-time) { background: #3076EE !important; }
.vuecal-custom.production :deep(.vuecal__event.format-pelet_3),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_3 .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_3 .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_3 .vuecal__event-time) { background: #E31431 !important; }
.vuecal-custom.production :deep(.vuecal__event.format-pelet_6),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_6 .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_6 .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_6 .vuecal__event-time) { background: #E87B37 !important; }
.vuecal-custom.production :deep(.vuecal__event.format-pelet_8),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_8 .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_8 .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_8 .vuecal__event-time) { background: #845FEF !important; }
.vuecal-custom.production :deep(.vuecal__event.format-pelet_12),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_12 .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_12 .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-pelet_12 .vuecal__event-time) { background: #EBA23D !important; }
.vuecal-custom.production :deep(.vuecal__event.format-other),
.vuecal-custom.production :deep(.vuecal__event.format-other .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.format-other .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.format-other .vuecal__event-time) { background: #3076EE !important; }

.vuecal-custom.production :deep(.vuecal__event.status-new),
.vuecal-custom.production :deep(.vuecal__event.status-new .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.status-new .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.status-new .vuecal__event-time),
.vuecal-custom.production :deep(.vuecal__event.status-processing),
.vuecal-custom.production :deep(.vuecal__event.status-processing .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.status-processing .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.status-processing .vuecal__event-time),
.vuecal-custom.production :deep(.vuecal__event.status-modified),
.vuecal-custom.production :deep(.vuecal__event.status-modified .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event.status-modified .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event.status-modified .vuecal__event-time) {
  background: #e5e7eb !important;
  color: #374151 !important;
  border-color: #d1d5db !important;
}

.vuecal-custom.production :deep(.vuecal__event.status-new:hover),
.vuecal-custom.production :deep(.vuecal__event.status-processing:hover),
.vuecal-custom.production :deep(.vuecal__event.status-modified:hover) {
  transform: none !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important;
}

.vuecal-custom.production :deep(.vuecal__event .vuecal__event-content),
.vuecal-custom.production :deep(.vuecal__event .vuecal__event-title),
.vuecal-custom.production :deep(.vuecal__event .vuecal__event-time) {
  background: transparent !important;
}

.vuecal-custom.production :deep(.vuecal__event.packaging-granel)   { border-left: 8px solid #020405; }
.vuecal-custom.production :deep(.vuecal__event.packaging-ensacado) { border-left: 8px solid #4CE51E; }
.vuecal-custom.production :deep(.vuecal__event.packaging-maxisaco) { border-left: 8px solid #0CEEE7; }
.vuecal-custom.production :deep(.vuecal__event.packaging-other)    { border-left: 8px solid #4b5563; }


.vuecal-custom.production :deep(.vuecal__event-title) {
  font-size: 9px;
  line-height: 1.1;
  white-space: normal;
}

.vuecal-custom.production :deep(.event-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.vuecal-custom.production :deep(.event-line-1) {
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  font-size: 10px;
  margin-bottom: 2px;
}

.vuecal-custom.production :deep(.event-line-2) {
  font-size: 9px;
  margin-top: 1px;
  margin-bottom: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vuecal-custom.production :deep(.event-line-3) {
  font-size: 8px;
  margin-top: 1px;
  margin-bottom: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
}

.vuecal-custom.production :deep(.event-line-4) {
  font-size: 8px;
  margin-top: 1px;
  margin-bottom: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.vuecal-custom.production :deep(.event-line-5) {
  font-size: 8px;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
}

/* Ocultar la hora del evento */
.vuecal-custom.production :deep(.vuecal__event-time) {
  display: none !important;
}

.vuecal-custom.production :deep(.vuecal__cell-events-count) {
  display: none;
}

/* Estilos para los botones de navegación */
.vuecal-custom.production :deep(.vuecal__arrow) {
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  background: #fff !important;
  color: #666 !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  height: 28px !important;
  width: 28px !important;
  margin: 0 4px !important;
  line-height: 1 !important;
  padding: 0 !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
  text-shadow: none !important;
  text-align: center !important;
  cursor: pointer !important;
}

.vuecal-custom.production :deep(.vuecal__arrow):hover {
  background: #f5f5f5 !important;
  border-color: #ccc !important;
  color: #333 !important;
}

/* Botón "Hoy" */
.vuecal-custom.production :deep(.vuecal__today-btn) {
  background: #fff !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  color: #666 !important;
  font-family: 'Source Sans Pro', sans-serif !important;
  font-size: 13px !important;
  font-weight: normal !important;
  padding: 4px 10px !important;
  margin: 0 8px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
  text-shadow: none !important;
  position: relative !important;
  right: auto !important;
  top: auto !important;
  transform: none !important;
  z-index: 50 !important;
  pointer-events: auto !important;
  cursor: pointer !important;
}

.vuecal-custom.production :deep(.vuecal__today-btn):hover {
  background: #f5f5f5 !important;
  border-color: #ccc !important;
  color: #333 !important;
}

/* Botones de vista (Año, Mes, Semana, Día) */
.vuecal-custom.production :deep(.vuecal__view-btn) {
  background: #fff !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  color: #666 !important;
  font-family: 'Source Sans Pro', sans-serif !important;
  font-size: 13px !important;
  font-weight: normal !important;
  padding: 4px 10px !important;
  margin: 0 2px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
  text-shadow: none !important;
  cursor: pointer !important;
}

.vuecal-custom.production :deep(.vuecal__view-btn):hover {
  background: #f5f5f5 !important;
  border-color: #ccc !important;
  color: #333 !important;
}

.vuecal-custom.production :deep(.vuecal__view-btn.vuecal__view-btn--active) {
  background: #4e4e51 !important;
  border-color: #4e4e51 !important;
  color: #fff !important;
  box-shadow: none !important;
  text-shadow: none !important;
}

/* Título del calendario */
.vuecal-custom.production :deep(.vuecal__title) {
  color: #666 !important;
  font-family: 'Source Sans Pro', sans-serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  position: absolute !important;
  justify-content: space-between !important;
  display: flex !important;
  align-items: center !important;
  left: 33% !important;
  margin: 0 !important;
  text-shadow: none !important;
  z-index: 10 !important;
  pointer-events: none !important;
}

/* Botones dentro del título (para cambiar vista) */
.vuecal-custom.production :deep(.vuecal__title button) {
  background: #e74c3c !important;
  border: 1px solid #e74c3c !important;
  color: #fff !important;
  border-radius: 4px !important;
  padding: 3px 8px !important;
  font-size: 13px !important;
  cursor: pointer !important;
  box-shadow: none !important;
  text-shadow: none !important;
  pointer-events: auto !important;
}

.vuecal-custom.production :deep(.vuecal__title button:hover) {
  background: #c0392b !important;
  border-color: #c0392b !important;
}

.vuecal-custom.production :deep(.vuecal__title-bar) {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 12px !important;
}

.vuecal-custom.production :deep(.calendar-nav-wrapper) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}

.vuecal-custom.production :deep(.calendar-nav__today) {
  margin: 0 12px !important;
  flex-shrink: 0;
}

.vuecal-custom.production :deep(.calendar-day-nav__btn) {
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  background: #fff !important;
  color: #666 !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  height: 28px !important;
  width: 28px !important;
  margin: 0 4px !important;
  line-height: 1 !important;
  padding: 0 !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
  text-shadow: none !important;
  text-align: center !important;
  cursor: pointer !important;
}

.vuecal-custom.production :deep(.calendar-day-nav__btn:hover) {
  background: #f5f5f5 !important;
  border-color: #ccc !important;
  color: #333 !important;
}

.vuecal-custom.production :deep(.calendar-nav-group) {
  display: flex;
  align-items: center;
  gap: 6px !important;
}

.vuecal-custom.production :deep(.calendar-nav-group--left) {
  justify-content: flex-start;
}

.vuecal-custom.production :deep(.calendar-nav-group--right) {
  flex: 1;
  justify-content: flex-end;
  margin-left: 12px;
}

@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
  }

  .filters-container {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
